import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import ESSLayout from './components/layout/ESSLayout';
import Spinner from './components/common/Spinner';
import './styles/global.css';

// Lazy-loaded pages
const Login       = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Setup       = lazy(() => import('./pages/Setup'));
const Dashboard   = lazy(() => import('./pages/Dashboard'));
const Employees      = lazy(() => import('./pages/Employees'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'));
const Departments    = lazy(() => import('./pages/Departments'));
const Leave       = lazy(() => import('./pages/Leave'));
const MedicalPermissions = lazy(() => import('./pages/MedicalPermissions'));
const GatePass       = lazy(() => import('./pages/GatePass'));
const Memos          = lazy(() => import('./pages/Memos'));
const Attendance  = lazy(() => import('./pages/Attendance'));
const Payroll     = lazy(() => import('./pages/Payroll'));
const WageSheets  = lazy(() => import('./pages/WageSheets'));
const LoansAdvances  = lazy(() => import('./pages/LoansAdvances'));
const Payslips    = lazy(() => import('./pages/Payslips'));
const Performance = lazy(() => import('./pages/Performance'));
const Recruitment = lazy(() => import('./pages/Recruitment'));
const Onboarding  = lazy(() => import('./pages/Onboarding'));
const Training    = lazy(() => import('./pages/Training'));
const Grievances  = lazy(() => import('./pages/Grievances'));
const Assets      = lazy(() => import('./pages/Assets'));
const Clearances  = lazy(() => import('./pages/Clearances'));
const AllowancesDeductions = lazy(() => import('./pages/AllowancesDeductions'));
const DisciplinaryManagement = lazy(() => import('./pages/DisciplinaryManagement'));
const PromotionsTransfers = lazy(() => import('./pages/PromotionsTransfers'));
const ContractsDocuments = lazy(() => import('./pages/ContractsDocuments'));
const Audit       = lazy(() => import('./pages/Audit'));
const Users       = lazy(() => import('./pages/Users'));
const System      = lazy(() => import('./pages/System'));
const Reports     = lazy(() => import('./pages/Reports'));
const Anomalies   = lazy(() => import('./pages/Anomalies'));
const NotFound    = lazy(() => import('./pages/NotFound'));

// ESS Lazy-loaded pages
const ESSDashboard    = lazy(() => import('./pages/ess/ESSDashboard'));
const ESSProfile      = lazy(() => import('./pages/ess/ESSProfile'));
const ESSLeave        = lazy(() => import('./pages/ess/ESSLeave'));
const ESSAttendance   = lazy(() => import('./pages/ess/ESSAttendance'));
const ESSPayslips     = lazy(() => import('./pages/ess/ESSPayslips'));
const ESSMemos        = lazy(() => import('./pages/ess/ESSMemos'));
const ESSLoans        = lazy(() => import('./pages/ess/ESSLoans'));
const ESSPerformance  = lazy(() => import('./pages/ess/ESSPerformance'));
const ESSGrievances   = lazy(() => import('./pages/ess/ESSGrievances'));
const ESSTraining     = lazy(() => import('./pages/ess/ESSTraining'));
const ESSMedical      = lazy(() => import('./pages/ess/ESSMedical'));
const ESSGatepass     = lazy(() => import('./pages/ess/ESSGatepass'));
const ESSNotifications = lazy(() => import('./pages/ess/ESSNotifications'));
const ESSHelpDesk      = lazy(() => import('./pages/ess/ESSHelpDesk'));

function getRoleRedirect(role) {
  if (role === 'employee') return '/ess';
  if (role === 'section_supervisor') return '/manager-dashboard';
  if (role === 'dept_manager') return '/department-dashboard';
  if (role === 'hr_officer' || role === 'hr_manager' || role === 'hr_director') return '/hr-dashboard';
  if (role === 'payroll_officer') return '/payroll-dashboard';
  if (role === 'system_admin') return '/admin-dashboard';
  return '/dashboard';
}

function ProtectedRoute({ children, module, action = 'view', isEss = false }) {
  const { user, loading, can } = useAuth();
  if (loading) return <Spinner fullscreen />;
  if (!user) {
    const redirectPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Employees are restricted from admin routes
  if (user.role === 'employee' && !isEss) {
    return <Navigate to="/ess" replace />;
  }

  // Permission check for admin/manager modules
  if (module && !isEss && !can(module, action)) {
    return <Navigate to={getRoleRedirect(user.role)} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner fullscreen />;
  if (user)    return <Navigate to={getRoleRedirect(user.role)} replace />;
  return children;
}

function IndexRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner fullscreen />;
  if (!user)   return <Navigate to="/login" replace />;
  return <Navigate to={getRoleRedirect(user.role)} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<Spinner fullscreen />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/" element={<IndexRedirect />} />

        {/* ── Employee Self Service (ESS) Namespace ── */}
        <Route element={<ProtectedRoute isEss><ESSLayout /></ProtectedRoute>}>
          <Route path="/ess" element={<ESSDashboard />} />
          <Route path="/ess/profile" element={<ESSProfile />} />
          <Route path="/ess/leave" element={<ESSLeave />} />
          <Route path="/ess/leave/balances" element={<ESSLeave />} />
          <Route path="/ess/attendance" element={<ESSAttendance />} />
          <Route path="/ess/shifts" element={<ESSAttendance />} />
          <Route path="/ess/payslips" element={<ESSPayslips />} />
          <Route path="/ess/documents" element={<ESSMemos />} />
          <Route path="/ess/loans" element={<ESSLoans />} />
          <Route path="/ess/performance" element={<ESSPerformance />} />
          <Route path="/ess/grievances" element={<ESSGrievances />} />
          <Route path="/ess/training" element={<ESSTraining />} />
          <Route path="/ess/medical" element={<ESSMedical />} />
          <Route path="/ess/gatepass" element={<ESSGatepass />} />
          <Route path="/ess/notifications" element={<ESSNotifications />} />
          <Route path="/ess/help-desk" element={<ESSHelpDesk />} />
          <Route path="/ess/settings" element={<ESSProfile />} />
        </Route>

        {/* Protected Admin/Manager modules */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard"   element={<Dashboard />} />
          <Route path="/manager-dashboard" element={<Dashboard />} />
          <Route path="/department-dashboard" element={<Dashboard />} />
          <Route path="/hr-dashboard" element={<Dashboard />} />
          <Route path="/payroll-dashboard" element={<Dashboard />} />
          <Route path="/admin-dashboard" element={<Dashboard />} />

          <Route path="/employees"   element={<ProtectedRoute module="employee_records"><Employees /></ProtectedRoute>} />
          <Route path="/employees/:id" element={<ProtectedRoute module="employee_records"><EmployeeProfile /></ProtectedRoute>} />
          <Route path="/departments" element={<ProtectedRoute module="org_chart"><Departments /></ProtectedRoute>} />
          <Route path="/departments-and-positions" element={<ProtectedRoute module="org_chart"><Departments /></ProtectedRoute>} />
          <Route path="/leave"       element={<ProtectedRoute module="leave_management"><Leave /></ProtectedRoute>} />
          <Route path="/leave-management" element={<ProtectedRoute module="leave_management"><Leave /></ProtectedRoute>} />
          <Route path="/medical"     element={<ProtectedRoute module="leave_management"><MedicalPermissions /></ProtectedRoute>} />
          <Route path="/gatepass"    element={<ProtectedRoute module="attendance_tracker"><GatePass /></ProtectedRoute>} />
          <Route path="/memos"       element={<ProtectedRoute module="employee_self_svc"><Memos /></ProtectedRoute>} />
          <Route path="/attendance"  element={<ProtectedRoute module="attendance_tracker"><Attendance /></ProtectedRoute>} />
          <Route path="/payroll"     element={<ProtectedRoute module="payroll_processing"><Payroll /></ProtectedRoute>} />
          <Route path="/wages"       element={<ProtectedRoute module="payroll_processing"><WageSheets /></ProtectedRoute>} />
          <Route path="/loans"       element={<ProtectedRoute module="payroll_processing"><LoansAdvances /></ProtectedRoute>} />
          <Route path="/payslips"    element={<ProtectedRoute module="payslip_generator"><Payslips /></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute module="performance_reviews"><Performance /></ProtectedRoute>} />
          <Route path="/recruitment" element={<ProtectedRoute module="recruitment_portal"><Recruitment /></ProtectedRoute>} />
          <Route path="/onboarding"  element={<ProtectedRoute module="onboarding_flow"><Onboarding /></ProtectedRoute>} />
          <Route path="/training"    element={<ProtectedRoute module="training_certs"><Training /></ProtectedRoute>} />
          <Route path="/grievances"  element={<ProtectedRoute module="grievance_mgmt"><Grievances /></ProtectedRoute>} />
          <Route path="/assets"      element={<ProtectedRoute module="employee_records"><Assets /></ProtectedRoute>} />
          <Route path="/clearances"  element={<ProtectedRoute module="employee_records"><Clearances /></ProtectedRoute>} />
          <Route path="/allowances-deductions" element={<ProtectedRoute module="payroll_processing"><AllowancesDeductions /></ProtectedRoute>} />
          <Route path="/disciplinary" element={<ProtectedRoute module="disciplinary_cases"><DisciplinaryManagement /></ProtectedRoute>} />
          <Route path="/promotions" element={<ProtectedRoute module="employee_records"><PromotionsTransfers /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute module="contract_manager"><ContractsDocuments /></ProtectedRoute>} />
          <Route path="/audit"       element={<ProtectedRoute module="audit_logs"><Audit /></ProtectedRoute>} />
          <Route path="/users"       element={<ProtectedRoute module="user_management"><Users /></ProtectedRoute>} />
          <Route path="/system"      element={<ProtectedRoute module="system_config"><System /></ProtectedRoute>} />
          <Route path="/reports"     element={<ProtectedRoute module="reports_analytics"><Reports /></ProtectedRoute>} />
          <Route path="/anomalies"   element={<ProtectedRoute module="reports_analytics"><Anomalies /></ProtectedRoute>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: "'Outfit', sans-serif", fontSize: '13.5px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
            success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#DC2626', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
